import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import ErrorBanner from '../../components/ErrorBanner';
import WorkspaceRoutes from '../WorkspaceRoutes';
import { browserGet, browserModeKey, browserSessionKey } from './client';
import { createScopedApiClient, type BrowserSession } from './originalUiClient';

export default function ToolBrowserOriginalWorkspace() {
  const location = useLocation();
  const [session, setSession] = useState<BrowserSession | null>(null);
  const [status, setStatus] = useState('Checking originating tool-token authority…');
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);
  const stopped = useRef(false);
  const loseSession = useCallback(() => {
    generation.current += 1;
    setSession(null);
    setError(null);
    setStatus('Browser authority changed or expired. Refresh access or open a tool-token login link again.');
  }, []);
  const apiClient = useMemo(() => {
    if (!session) return null;
    const admittedGeneration = generation.current;
    return createScopedApiClient(session, () => {
      // A rejected response from a previous refresh cannot clear a later view.
      if (generation.current === admittedGeneration) loseSession();
    });
  }, [loseSession, session]);
  const refresh = useCallback(async () => {
    if (stopped.current) return;
    const request = ++generation.current;
    setSession(null);
    setError(null);
    try {
      sessionStorage.setItem(browserModeKey, 'scoped');
      const expected = sessionStorage.getItem(browserSessionKey);
      if (!expected) throw new Error('Open a tool-token login link to start a browser session.');
      const current = await browserGet<BrowserSession>('/api/browser/session');
      if (current.session_id !== expected || sessionStorage.getItem(browserSessionKey) !== expected) {
        throw new Error('Browser session changed. Open a tool-token login link again.');
      }
      if (generation.current !== request || stopped.current) return;
      setSession(current);
      setStatus('Mission Control — originating tool-token authority only.');
    } catch (failure) {
      if (generation.current !== request || stopped.current) return;
      setStatus(failure instanceof Error ? failure.message : 'Browser authority unavailable.');
    }
  }, []);
  useEffect(() => {
    let canceled = false;
    stopped.current = false;
    queueMicrotask(() => { if (!canceled) void refresh(); });
    const timer = window.setInterval(() => void refresh(), 60_000);
    const foreground = () => { if (document.visibilityState === 'visible') void refresh(); };
    document.addEventListener('visibilitychange', foreground);
    return () => {
      canceled = true; stopped.current = true; generation.current += 1;
      window.clearInterval(timer); document.removeEventListener('visibilitychange', foreground);
    };
  }, [refresh]);
  async function logout() {
    generation.current += 1;
    stopped.current = true;
    const current = session;
    setSession(null); setError(null);
    sessionStorage.removeItem(browserSessionKey);
    setStatus('Tool-token browser session closed. Open a new login link to continue.');
    if (current) await fetch('/api/browser/session', {
      method: 'DELETE', credentials: 'same-origin', cache: 'no-store',
      headers: { 'X-Browser-Session-Id': current.session_id, 'X-Browser-CSRF': current.csrf_token }
    }).catch(() => undefined);
    // Keep the mode latch: navigation cannot turn logout into stronger SSO.
  }
  const conversationRoute = location.pathname.includes('/conversations');
  const conversationDetail = /\/conversations\/[^/]+$/.test(location.pathname);
  return <div className={['shell', 'shell--with-nav', 'shell--tool-browser', conversationRoute ? 'shell--conversation-route' : '',
    conversationDetail ? 'shell--conversation-detail-route' : ''].filter(Boolean).join(' ')}>
    <aside className="sidebar">
      <div className="sidebar-brand"><img className="sidebar-brand-logo" src="/brand/yaaf-wordmark.svg" alt="YAAF" /><div><strong>Mission Control</strong><span>Tool-token authority</span></div></div>
      <nav className="sidebar-nav" aria-label="Main navigation">
        <NavLink to="/conversations">Conversations</NavLink>
        <NavLink to="/apps">Apps</NavLink>
        <NavLink to="/terminals">Terminals</NavLink>
        <NavLink to="/executors">Executors</NavLink>
        <NavLink to="/monitoring">Monitoring</NavLink>
        <NavLink to="/jobs">Jobs</NavLink>
        <NavLink to="/logs">Logs</NavLink>
        <NavLink to="/settings">Settings</NavLink>
        {apiClient?.browserGrantManagement && <NavLink to="/settings/tokens">Token execution grants</NavLink>}
      </nav>
    </aside>
    <div className="app">
      <header className="topbar">
        <div><h1>Mission Control</h1><p role="status">{status}</p></div>
        {session && <p>{session.subject.name ?? session.subject.email ?? session.subject.id}</p>}
        {!stopped.current && <button onClick={() => void refresh()}>Refresh access</button>}
        {session && <button onClick={() => void logout()}>Logout</button>}
      </header>
      <main className="content">
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
        {session && apiClient ? <WorkspaceRoutes
          key={session.session_id} apiClient={apiClient} setError={setError}
          isAdmin={false} canManageApps={false} canCreate={session.project_access}
          canReadConversations={session.capabilities.conversations_read}
          canViewExecutors={session.capabilities.executors_read === true} canOperateExecutors={false}
          canViewExecutorNodes={session.capabilities.executors_read === true}
          canMaintainHosts={false} canUseExecutorTerminal={false}
          canUsePrivilegedExecutorTerminal={false} canManageExecutorTerminalIdentities={false}
        /> : <div className="card"><h2>Tool-token browser</h2><p role="status">{status}</p><p>No ordinary sign-in or REST requests are made in this mode.</p></div>}
      </main>
    </div>
  </div>;
}
